import manifest from "@/assets/image-manifest.json";
import type { ReactEventHandler } from "react";
import { cn } from "@/lib/cn";
import { resolveImageKey } from "./image-fallback";

export type ImgAspect = "21/9" | "3/2" | "4/3" | "16/10" | "16/9" | "16/5" | "1/1";

export interface ImgManifestEntry {
  width: number;
  height: number;
  widths: number[];
}

const MANIFEST = manifest as Record<string, ImgManifestEntry>;

export interface ImgProps {
  imageKey: string;
  alt: string;
  aspect: ImgAspect;
  sizes: string;
  priority?: boolean;
  fill?: boolean;
  className?: string;
  imgClassName?: string;
  onLoad?: ReactEventHandler<HTMLImageElement>;
}

/**
 * Fixed-geometry responsive image. Renders AVIF then WebP srcsets from
 * `image-manifest.json`; an unknown key falls back to another manifest entry
 * in the same family (see image-fallback.ts), or just the placeholder box.
 */
export function Img({
  imageKey,
  alt,
  aspect,
  sizes,
  priority = false,
  fill = false,
  className,
  imgClassName,
  onLoad,
}: ImgProps) {
  const resolvedKey = resolveImageKey(imageKey, Object.keys(MANIFEST));
  const entry = resolvedKey ? MANIFEST[resolvedKey] : undefined;

  return (
    <div
      className={cn("overflow-hidden bg-surface-2", fill && "absolute inset-0", className)}
      style={fill ? undefined : { aspectRatio: aspect.replace("/", " / ") }}
    >
      {entry && resolvedKey && (
        <picture className={fill ? "block size-full" : undefined}>
          <source type="image/avif" srcSet={entry.widths.map((w) => `/img/${resolvedKey}-${w}.avif ${w}w`).join(", ")} sizes={sizes} />
          <source type="image/webp" srcSet={entry.widths.map((w) => `/img/${resolvedKey}-${w}.webp ${w}w`).join(", ")} sizes={sizes} />
          <img
            src={`/img/${resolvedKey}-${entry.widths[entry.widths.length - 1]}.webp`}
            alt={alt}
            width={entry.width}
            height={entry.height}
            className={cn("size-full object-cover", imgClassName)}
            loading={priority ? "eager" : "lazy"}
            decoding={priority ? undefined : "async"}
            fetchPriority={priority ? "high" : undefined}
            onLoad={onLoad}
          />
        </picture>
      )}
    </div>
  );
}
